import React, { useState, useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import LoadingSpinner from "@/components/loading/LoadingSpinner";
import "./Map.css";

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const Map = ({ initialLocation, onLocationChange }) => {
  const [departureLocation, setDepartureLocation] = useState(
    initialLocation || ""
  );
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [coordinates, setCoordinates] = useState({ lat: null, lon: null });

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const locationCache = useRef({});
  const mapInitialized = useRef(false);

  const initMap = useCallback(() => {
    if (mapRef.current) return;

    const vietnamBounds = [
      [8.18, 102.14],
      [23.39, 109.47],
    ];

    const map = L.map("map", {
      maxBounds: vietnamBounds,
      maxBoundsViscosity: 1.0,
      minZoom: 5,
    }).setView([16.0471, 108.2062], 6);

    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    markerRef.current = L.marker([16.0471, 108.2062]).addTo(map);
  }, []);

  const updateMap = (lat, lon) => {
    if (mapRef.current && markerRef.current) {
      const map = mapRef.current;
      const marker = markerRef.current;
      const latNum = parseFloat(lat);
      const lonNum = parseFloat(lon);

      const vietnamBounds = [
        [8.18, 102.14],
        [23.39, 109.47],
      ];
      if (
        latNum >= vietnamBounds[0][0] &&
        latNum <= vietnamBounds[1][0] &&
        lonNum >= vietnamBounds[0][1] &&
        lonNum <= vietnamBounds[1][1]
      ) {
        map.setView([latNum, lonNum], 13);
        marker.setLatLng([latNum, lonNum]);
        setCoordinates({ lat: latNum, lon: lonNum });
      }
    }
  };

  const fetchLocation = async (query, initialLoad = false) => {
    if (locationCache.current[query]) {
      if (!initialLoad) {
        setSuggestions(locationCache.current[query]);
      }
      if (initialLoad && locationCache.current[query].length > 0) {
        const { lat, lon } = locationCache.current[query][0];
        updateMap(lat, lon);
      }
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          query
        )}&format=json&limit=10&countrycodes=vn&addressdetails=1`
      );
      const data = await response.json();
      locationCache.current[query] = data;
      if (!initialLoad) {
        setSuggestions(data);
      }
      if (initialLoad && data.length > 0) {
        const { lat, lon } = data[0];
        updateMap(lat, lon);
      }
    } catch (error) {
      console.error("Error fetching location:", error);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const debouncedFetchLocation = useCallback(debounce(fetchLocation, 500), []);

  useEffect(() => {
    if (!mapInitialized.current) {
      initMap();
      mapInitialized.current = true;
    }

    if (initialLocation && initialLocation !== departureLocation) {
      setDepartureLocation(initialLocation);
      fetchLocation(initialLocation, true);
    }
  }, [initialLocation, initMap]);

  const handleDepartureLocationChange = (e) => {
    const value = e.target.value;
    setDepartureLocation(value);
    onLocationChange(value); // Gửi giá trị thô khi

    if (value.length > 3) {
      debouncedFetchLocation(value);
    } else {
      setSuggestions([]);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    const selectedLocation = suggestion.display_name;
    const lat = parseFloat(suggestion.lat).toFixed(6);
    const lon = parseFloat(suggestion.lon).toFixed(6);
    // Định dạng chuỗi theo yêu cầu: "Đà Nẵng, Vietnam (Tọa độ: Lat 16.068000, Lon 108.212000)"
    const formattedLocation = `${selectedLocation} (Tọa độ: Lat ${lat}, Lon ${lon})`;

    setDepartureLocation(formattedLocation);
    updateMap(suggestion.lat, suggestion.lon);
    setSuggestions([]);
    onLocationChange(formattedLocation); // Gửi chuỗi đã định dạng lên parent
  };

  return (
    <div className="map-component">
      <div className="mb-3 position-relative">
        <label className="form-label">Địa chỉ của bạn</label>
        <input
          type="text"
          className="form-control"
          placeholder="Nhập địa chỉ của bạn ..."
          value={departureLocation}
          onChange={handleDepartureLocationChange}
        />
        {isLoadingSuggestions && (
          <LoadingSpinner
            type="circle"
            color="#3498db"
            size={20}
            text="Đang tìm kiếm..."
          />
        )}
        {suggestions.length > 0 && !isLoadingSuggestions && (
          <ul className="suggestions-list">
            {suggestions.map((suggestion, idx) => (
              <li key={idx} onClick={() => handleSelectSuggestion(suggestion)}>
                {suggestion.display_name}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mb-3">
        <label className="form-label">Bản đồ địa điểm khởi hành</label>
        <div id="map" style={{ height: "300px", width: "100%" }}></div>
      </div>
      {coordinates.lat && coordinates.lon && (
        <div className="coordinates">
          <p>
            Tọa độ: Lat {coordinates.lat.toFixed(6)}, Lon{" "}
            {coordinates.lon.toFixed(6)}
          </p>
        </div>
      )}
    </div>
  );
};

export default Map;
